import { useId, useState, useMemo } from 'react';
import { Sparkles, X, Trash2 } from 'lucide-react';
import { TICKET_STATUS, TICKET_STATUS_ORDER } from './ticketStatus';
import { buildClaudePrompt } from './bugReportHelpers';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// BugDetailModal — full detail view for a single ticket: status pipeline,
// description, screenshot, comment thread, captured console log, context,
// the Claude prompt, and metadata. Extracted verbatim from the original
// artifact (legacy/App.jsx). All persistence happens via the callbacks supplied by
// BugArchiveView (onSetStatus / onAddComment / onDelete / onOpenImage), so this
// component touches no Storage directly. No secrets here.
// ─────────────────────────────────────────────────────────────────────

export interface BugDetailModalProps {
  bug: any;
  onClose: any;
  onSetStatus: any;
  onAddComment: any;
  onOpenImage?: any;
  currentStatus: any;
  onDelete: any;
  copyStatus: any;
  setCopyStatus: any;
}

// Modal that shows the full Claude prompt for a bug
export function BugDetailModal({
  bug,
  onClose,
  onSetStatus,
  onAddComment,
  onOpenImage,
  currentStatus,
  onDelete,
  copyStatus,
  setCopyStatus,
}: BugDetailModalProps) {
  const t = useT();
  const uid = safeId('bug-detail', useId());
  const ids = {
    title: `${uid}-title`,
    status: `${uid}-status`,
    author: `${uid}-author`,
    comment: `${uid}-comment`,
  };
  // Local state for the new-comment field
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const [commentAuthor, setCommentAuthor] = useState('owner'); // 'owner' | 'user'

  // Build the combined comment list — include the legacy ownerComment if present
  const commentList = useMemo(() => {
    const list = Array.isArray(bug.comments) ? [...bug.comments] : [];
    if (bug.ownerComment && list.length === 0) {
      list.push({
        id: `legacy_${bug.id}`,
        text: bug.ownerComment,
        author: 'owner',
        createdAt: bug.ownerCommentAt || bug.createdAt,
      });
    }
    return list.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  }, [bug.comments, bug.ownerComment, bug.ownerCommentAt, bug.createdAt, bug.id]);

  const handleSendComment = async () => {
    if (!commentDraft.trim() || commentSending) return;
    setCommentSending(true);
    await onAddComment(commentDraft.trim(), commentAuthor);
    setCommentDraft('');
    setCommentSending(false);
  };

  // One paste must be enough to act on: page/context + the environment it
  // reproduced in + the session console log + whether a screenshot exists.
  const claudePrompt = buildClaudePrompt(
    bug.page,
    bug.view,
    bug.context,
    bug.title,
    bug.description,
    bug.kind,
    {
      runtime: bug.runtime,
      consoleLog: bug.consoleLog,
      hasScreenshot: !!bug.screenshot,
      // `?? null`: an old report without a trail must not borrow the viewer's own.
      actions: bug.actions ?? null,
      failedRequests: bug.failedRequests ?? null,
    },
  );
  const statusInfo = TICKET_STATUS[currentStatus] || TICKET_STATUS.open;

  // Copy the screenshot itself to the clipboard, so the report can be pasted as
  // prompt + image without opening the archive and saving the file by hand.
  const [shotStatus, setShotStatus] = useState('');
  const handleCopyScreenshot = async () => {
    try {
      const res = await fetch(bug.screenshot); // base64 data URL → Blob
      const blob = await res.blob();
      // Clipboard images must be PNG; re-encode anything else via a canvas.
      let png = blob;
      if (blob.type !== 'image/png') {
        png = await new Promise<Blob>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d')!.drawImage(img, 0, 0);
            c.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
          };
          img.onerror = () => reject(new Error('kunne ikke laste skjermbildet'));
          img.src = bug.screenshot;
        });
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      setShotStatus('copied');
      setTimeout(() => setShotStatus(''), 2000);
    } catch (e) {
      console.error('[Padel24] Kunne ikke kopiere skjermbildet:', e);
      setShotStatus('error');
      setTimeout(() => setShotStatus(''), 2500);
    }
  };

  const handleCopy = async (text: any) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (e) {
      console.error('[Padel24] Kunne ikke kopiere:', e);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  return (
    // A `Dialog` (#900), named by the ticket's title. Escape and the backdrop
    // close it, and focus goes back to the row that opened it. The full-size
    // screenshot opens ON TOP of it and owns the keyboard while it is up.
    <Dialog
      labelledBy={ids.title}
      onClose={onClose}
      showClose={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      className="card w-full max-w-2xl flex flex-col"
      style={{ maxHeight: '92vh' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-line flex-shrink-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {bug.kind === 'feature' ? (
              <span
                className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
                style={{ background: '#dcfce7', color: '#15803d' }}
              >
                <Sparkles size={9} strokeWidth={3} /> FORSLAG
              </span>
            ) : (
              <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-soft text-rose">
                BUG
              </span>
            )}
            <span
              className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{
                background: statusInfo.bg,
                color: statusInfo.fg,
                border: `1px solid ${statusInfo.border}`,
              }}
            >
              {statusInfo.label.toUpperCase()}
            </span>
            <span className="font-mono text-[10px] text-ink-3">{bug.page}</span>
            <span className="font-mono text-[10px] text-ink-4">·</span>
            <span className="font-mono text-[10px] text-ink-3">
              {new Date(bug.createdAt).toLocaleString('no-NO', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <h3
            id={ids.title}
            className={`font-display text-xl font-semibold leading-tight ${currentStatus === 'closed' || currentStatus === 'rejected' ? 'line-through text-ink-3' : ''}`}
          >
            {bug.title}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('ui.dialog.close')}
          className="p-1 text-ink-3 hover:text-ink-2 flex-shrink-0"
        >
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Status pipeline selector */}
        {/* A title over a row of choice buttons: a named group, and each
              button says whether it is the current status (#900). */}
        <div
          className="rounded-lg border border-line p-3 bg-bg-soft/30"
          role="group"
          aria-labelledby={ids.status}
        >
          <span
            id={ids.status}
            className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2 block"
          >
            Status
          </span>
          <div className="flex flex-wrap gap-1.5">
            {TICKET_STATUS_ORDER.map((sId) => {
              const s = TICKET_STATUS[sId];
              const selected = currentStatus === sId;
              return (
                <button
                  key={sId}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSetStatus(sId)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5"
                  style={
                    selected
                      ? { background: s.bg, color: s.fg, border: `1.5px solid ${s.border}` }
                      : { background: 'white', color: '#94a098', border: '1.5px solid #d4dad6' }
                  }
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: selected ? s.border : '#d4dad6' }}
                  />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block">
            Beskrivelse
          </div>
          <div className="rounded-lg border border-line p-3 bg-bg-soft/40 text-sm whitespace-pre-wrap">
            {bug.description}
          </div>
        </div>

        {/* Screenshot — clicking opens a full-screen preview */}
        {bug.screenshot && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block">
              Skjermbilde
            </div>
            <button
              onClick={() => onOpenImage && onOpenImage(bug.screenshot, bug.title)}
              className="block w-full rounded-lg border border-line overflow-hidden hover:border-ink-4 transition-colors cursor-zoom-in"
              title="Klikk for å se full størrelse"
            >
              <img
                src={bug.screenshot}
                alt="Skjermbilde fra rapporten"
                className="w-full max-h-96 object-contain bg-bg-soft"
              />
            </button>
          </div>
        )}

        {/* Comment thread */}
        <div className="rounded-lg border border-line p-3 bg-bg-soft/30">
          <h3 className="font-display text-sm font-semibold mb-2 flex items-center gap-2">
            Kommentarer
            {commentList.length > 0 && (
              <span className="font-mono text-[10px] text-ink-3 bg-paper px-1.5 py-0.5 rounded-full border border-line">
                {commentList.length}
              </span>
            )}
          </h3>

          {/* Thread */}
          {commentList.length > 0 ? (
            <div className="space-y-2 mb-3">
              {commentList.map((c) => (
                <div
                  key={c.id}
                  className="px-3 py-2 rounded-lg text-[12px] leading-relaxed"
                  style={{
                    background:
                      c.author === 'owner'
                        ? 'rgba(220, 252, 231, 0.6)'
                        : 'rgba(219, 234, 254, 0.6)',
                    borderLeft: `3px solid ${c.author === 'owner' ? '#86efac' : '#93c5fd'}`,
                  }}
                >
                  <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-1 flex items-center justify-between">
                    <span>{c.author === 'owner' ? 'Andreas' : 'Rapportør'}</span>
                    <span className="text-ink-3">
                      {new Date(c.createdAt).toLocaleString('no-NO', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="text-ink-2 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-ink-3 italic mb-3 px-2 py-1">
              Ingen kommentarer ennå — skriv den første!
            </div>
          )}

          {/* New comment */}
          <div className="space-y-2 pt-2 border-t border-line-soft">
            <div className="flex items-center gap-2" role="group" aria-labelledby={ids.author}>
              <span
                id={ids.author}
                className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
              >
                Send som
              </span>
              <div className="flex gap-1 p-0.5 rounded-full border border-line bg-paper text-[10px]">
                <button
                  type="button"
                  aria-pressed={commentAuthor === 'owner'}
                  onClick={() => setCommentAuthor('owner')}
                  className={`px-2 py-0.5 rounded-full font-medium transition-all ${commentAuthor === 'owner' ? 'bg-ink text-paper' : 'text-ink-2 hover:text-ink'}`}
                >
                  Andreas
                </button>
                <button
                  type="button"
                  aria-pressed={commentAuthor === 'user'}
                  onClick={() => setCommentAuthor('user')}
                  className={`px-2 py-0.5 rounded-full font-medium transition-all ${commentAuthor === 'user' ? 'bg-ink text-paper' : 'text-ink-2 hover:text-ink'}`}
                >
                  Rapportør
                </button>
              </div>
            </div>
            <textarea
              id={ids.comment}
              aria-label={t('bugs.detail.newComment')}
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSendComment();
                }
              }}
              placeholder="Skriv en kommentar... (Ctrl/Cmd+Enter for å sende)"
              rows={3}
              className="input text-sm w-full resize-none"
            />
            <div className="flex justify-end">
              <button
                onClick={handleSendComment}
                disabled={!commentDraft.trim() || commentSending}
                className="btn-lime text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {commentSending ? 'Sender...' : 'Send kommentar'}
              </button>
            </div>
          </div>
        </div>

        {/* Console log — if captured at submission time */}
        {bug.consoleLog && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                Konsoll-logg (fra rapporterings-sesjonen)
              </span>
              <button
                onClick={() => handleCopy(bug.consoleLog)}
                className="text-[11px] font-mono text-ink-3 hover:text-ink-2 px-2 py-1 rounded hover:bg-bg-soft"
              >
                {copyStatus === 'copied' ? '✓ kopiert!' : '📋 kopiér'}
              </button>
            </div>
            <div className="rounded-lg border border-line p-3 bg-bg-soft/40 max-h-48 overflow-y-auto">
              <pre className="text-[10px] font-mono text-ink-2 whitespace-pre-wrap leading-relaxed">
                {bug.consoleLog}
              </pre>
            </div>
          </div>
        )}

        {/* Context */}
        {bug.context && Object.keys(bug.context).length > 0 && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 block">
              Kontekst
            </div>
            <div className="rounded-lg border border-line p-3 bg-bg-soft/40 font-mono text-xs space-y-0.5">
              {Object.entries(bug.context).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <span className="text-ink-3">{k}:</span>
                  <span className="text-ink-2">
                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Claude-prompt */}
        <div>
          {/* This is THE action in the archive — copy, paste to Claude, done.
                It used to read as a caption with two ghost links; now it leads. */}
          <div
            className="rounded-lg border p-3 mb-2"
            style={{ background: '#f5f3ff', borderColor: '#ddd6fe' }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-1">
                  <Sparkles size={13} style={{ color: '#7c3aed' }} /> Send til Claude
                </div>
                <p className="text-[11px] text-ink-3 mt-0.5">
                  Kopiér prompten og lim den inn i Claude — den inneholder side, miljø og
                  konsoll-logg.
                  {bug.screenshot ? ' Lim inn skjermbildet rett etterpå.' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleCopy(claudePrompt)}
                  className="text-[12px] font-semibold px-3.5 py-2 rounded-lg transition-colors"
                  style={{ background: '#7c3aed', color: '#fff' }}
                >
                  {copyStatus === 'copied' ? '✓ Kopiert!' : '📋 Kopiér prompt'}
                </button>
                {/* The screenshot has to be a SEPARATE copy: one ClipboardItem holding
                    both text and an image means "the same content in two formats", so
                    the paste target picks one and you lose the other. Two buttons =
                    paste twice, which is the only way to land both in a chat. */}
                {bug.screenshot && (
                  <button
                    onClick={handleCopyScreenshot}
                    className="text-[12px] font-semibold px-3.5 py-2 rounded-lg transition-colors"
                    style={{ background: '#fff', color: '#6d28d9', border: '1px solid #ddd6fe' }}
                    title="Kopierer skjermbildet — lim det inn rett etter prompten"
                  >
                    {shotStatus === 'copied'
                      ? '✓ Bilde kopiert!'
                      : shotStatus === 'error'
                        ? '✕ Kunne ikke kopiere'
                        : '🖼 Kopiér skjermbilde'}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-line p-3 bg-bg-soft/40 max-h-80 overflow-y-auto">
            <pre className="text-[11px] font-mono text-ink-2 whitespace-pre-wrap leading-relaxed">
              {claudePrompt}
            </pre>
          </div>
        </div>

        {/* Metadata */}
        <div className="rounded-lg border border-line p-3 text-[11px] font-mono space-y-1">
          <div className="flex justify-between gap-2">
            <span className="text-ink-3">ID</span>
            <span className="text-ink-2 truncate ml-2" style={{ maxWidth: '70%' }}>
              {bug.id}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-ink-3">View</span>
            <span className="text-ink-2">{bug.view}</span>
          </div>
          {bug.fixedAt && (
            <div className="flex justify-between gap-2">
              <span className="text-ink-3">Fullført</span>
              <span className="text-ink-2">{new Date(bug.fixedAt).toLocaleString('no-NO')}</span>
            </div>
          )}
          {bug.rejectedAt && (
            <div className="flex justify-between gap-2">
              <span className="text-ink-3">Avvist</span>
              <span className="text-ink-2">{new Date(bug.rejectedAt).toLocaleString('no-NO')}</span>
            </div>
          )}
          {bug.userAgent && (
            <div className="flex justify-between gap-2">
              <span className="text-ink-3">Nettleser</span>
              <span
                className="text-ink-2 truncate ml-2"
                style={{ maxWidth: '60%' }}
                title={bug.userAgent}
              >
                {bug.userAgent.split(' ').slice(-2).join(' ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-2 justify-between px-5 py-3 border-t border-line flex-shrink-0 flex-wrap">
        <button type="button" onClick={onDelete} className="btn-danger text-xs">
          <Trash2 size={12} /> Slett
        </button>
        <button type="button" onClick={onClose} className="btn-outline text-sm">
          Lukk
        </button>
      </div>
    </Dialog>
  );
}
